import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, DatePicker, TimePicker, Modal, Form, Typography, message, Popconfirm, Select, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined, FireOutlined, BellOutlined } from '@ant-design/icons';
import { colors, spacing, radius, typography } from '../../styles/monochromeTheme';
import { supabase } from '../../api/supabase';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const TEAM_MEMBERS = ['Félicie', 'Pierre', 'Hannah', 'Laurie', 'Gladys', 'Clem', 'Baptiste'];

const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=';

const SurLeFeu = () => {
  const [todayTasks, setTodayTasks] = useState([]);
  const [longTermTasks, setLongTermTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskType, setTaskType] = useState('today');
  const [form] = Form.useForm();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [hideCompleted, setHideCompleted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    checkNotificationPermission();
    const savedPref = localStorage.getItem('surLeFeuNotifications');
    if (savedPref === 'true') setNotificationsEnabled(true);
  }, []);

  useEffect(() => {
    fetchTasks();

    const subscription = supabase
      .channel('tasks_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tasks' }, (payload) => {
        const newTask = payload.new;
        if (newTask.type === 'today') {
          setTodayTasks(prev => [newTask, ...prev.filter(t => t.id !== newTask.id)]);
        } else {
          setLongTermTasks(prev => [newTask, ...prev.filter(t => t.id !== newTask.id)]);
        }
        showNotification(newTask);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tasks' }, (payload) => {
        const updated = payload.new;
        if (updated.type === 'today') {
          setTodayTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
          setLongTermTasks(prev => prev.filter(t => t.id !== updated.id));
        } else {
          setLongTermTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
          setTodayTasks(prev => prev.filter(t => t.id !== updated.id));
        }
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'tasks' }, (payload) => {
        const deletedId = payload.old.id;
        setTodayTasks(prev => prev.filter(t => t.id !== deletedId));
        setLongTermTasks(prev => prev.filter(t => t.id !== deletedId));
      })
      .subscribe();

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!notificationsEnabled) return;
    const checkScheduled = () => {
      const now = dayjs();
      const currentTime = now.format('HH:mm');
      [...todayTasks, ...longTermTasks].forEach(task => {
        if (task.notification_time === currentTime && !task.completed) {
          const key = `notif_${task.id}_${task.notification_time}`;
          const today = now.format('YYYY-MM-DD');
          if (localStorage.getItem(key) !== today) {
            showScheduledNotification(task);
            localStorage.setItem(key, today);
          }
        }
      });
    };
    checkScheduled();
    const interval = setInterval(checkScheduled, 60000);
    return () => clearInterval(interval);
  }, [todayTasks, longTermTasks, notificationsEnabled]);

  const checkNotificationPermission = async () => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') setHasPermission(true);
      else if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        setHasPermission(permission === 'granted');
      }
    }
  };

  const toggleNotifications = async () => {
    if (!hasPermission && !notificationsEnabled) {
      await checkNotificationPermission();
      if (!hasPermission) {
        message.warning('Autorisez les notifications dans votre navigateur');
        return;
      }
    }
    const newState = !notificationsEnabled;
    setNotificationsEnabled(newState);
    localStorage.setItem('surLeFeuNotifications', newState.toString());
  };

  const playSound = () => audioRef.current?.play().catch(() => {});

  const showNotification = (task) => {
    if (!notificationsEnabled || !hasPermission) return;
    playSound();
    const n = new Notification('🔥 Nouvelle tâche', {
      body: `${task.created_by}: ${task.title}`,
      icon: '/favicon.ico',
    });
    setTimeout(() => n.close(), 5000);
  };

  const showScheduledNotification = (task) => {
    if (!notificationsEnabled || !hasPermission) return;
    playSound();
    new Notification('⏰ Rappel', {
      body: `${task.notification_time} - ${task.title}`,
      icon: '/favicon.ico',
      requireInteraction: true,
    });
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('tasks').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setTodayTasks(data.filter(t => t.type === 'today'));
      setLongTermTasks(data.filter(t => t.type === 'long_term'));
    } catch (error) {
      message.error('Erreur de chargement');
    }
    setLoading(false);
  };

  const handleAdd = (type) => {
    setTaskType(type);
    setEditingTask(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (e, task) => {
    e.stopPropagation();
    setEditingTask(task);
    setTaskType(task.type);
    form.setFieldsValue({
      title: task.title,
      description: task.description,
      created_by: task.created_by,
      date: task.date ? dayjs(task.date) : null,
      notification_time: task.notification_time ? dayjs(task.notification_time, 'HH:mm') : null,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const taskData = {
        title: values.title,
        description: values.description || null,
        created_by: values.created_by,
        date: values.date ? values.date.format('YYYY-MM-DD') : null,
        notification_time: values.notification_time ? values.notification_time.format('HH:mm') : null,
        type: taskType,
      };

      if (editingTask) {
        const updated = { ...editingTask, ...taskData, updated_at: new Date().toISOString() };
        if (taskType === 'today') {
          setTodayTasks(prev => prev.map(t => t.id === editingTask.id ? updated : t));
        } else {
          setLongTermTasks(prev => prev.map(t => t.id === editingTask.id ? updated : t));
        }
        await supabase.from('tasks').update(taskData).eq('id', editingTask.id);
      } else {
        const tempId = `temp-${Date.now()}`;
        const optimistic = { ...taskData, id: tempId, created_at: new Date().toISOString(), completed: false };
        if (taskType === 'today') {
          setTodayTasks(prev => [optimistic, ...prev]);
        } else {
          setLongTermTasks(prev => [optimistic, ...prev]);
        }
        const { data, error } = await supabase.from('tasks').insert([taskData]).select().single();
        if (error) throw error;
        if (taskType === 'today') {
          setTodayTasks(prev => prev.map(t => t.id === tempId ? data : t));
        } else {
          setLongTermTasks(prev => prev.map(t => t.id === tempId ? data : t));
        }
      }

      setModalVisible(false);
      form.resetFields();
      message.success(editingTask ? 'Tâche modifiée' : 'Tâche ajoutée');
    } catch (error) {
      message.error('Erreur');
      fetchTasks();
    }
  };

  const handleToggle = async (task) => {
    const updated = { ...task, completed: !task.completed };
    if (task.type === 'today') {
      setTodayTasks(prev => prev.map(t => t.id === task.id ? updated : t));
    } else {
      setLongTermTasks(prev => prev.map(t => t.id === task.id ? updated : t));
    }
    await supabase.from('tasks').update({ completed: !task.completed }).eq('id', task.id);
  };

  const handleDelete = async (e, taskId, type) => {
    e.stopPropagation();
    if (type === 'today') {
      setTodayTasks(prev => prev.filter(t => t.id !== taskId));
    } else {
      setLongTermTasks(prev => prev.filter(t => t.id !== taskId));
    }
    await supabase.from('tasks').delete().eq('id', taskId);
    message.success('Supprimé');
  };

  const TaskItem = ({ task }) => {
    const isExpanded = expandedTaskId === task.id;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '12px 16px',
          background: task.completed ? '#fafafa' : '#fff',
          borderRadius: '8px',
          marginBottom: '8px',
          border: `1px solid ${isExpanded ? '#1890ff' : task.completed ? '#e8e8e8' : '#f0f0f0'}`,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: isExpanded ? '0 2px 8px rgba(24,144,255,0.15)' : 'none',
        }}
        onClick={() => setExpandedTaskId(isExpanded ? null : task.id)}
        onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = task.completed ? '#f5f5f5' : '#fafafa'; }}
        onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = task.completed ? '#fafafa' : '#fff'; }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '15px',
              fontWeight: 500,
              color: task.completed ? '#999' : '#1a1a1a',
              textDecoration: task.completed ? 'line-through' : 'none',
              marginBottom: '6px',
              lineHeight: 1.4,
            }}>
              {task.title}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#ff7a45',
                background: '#fff7e6',
                padding: '2px 8px',
                borderRadius: '4px',
              }}>
                {task.created_by}
              </span>

              <span style={{ fontSize: '11px', color: '#999' }}>
                {dayjs(task.created_at).fromNow()}
              </span>

              {task.date && (
                <span style={{ fontSize: '11px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CalendarOutlined style={{ fontSize: '10px' }} />
                  {dayjs(task.date).format('DD/MM')}
                </span>
              )}

              {task.notification_time && (
                <span style={{ fontSize: '11px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <BellOutlined style={{ fontSize: '10px' }} />
                  {task.notification_time}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            <Button
              type={task.completed ? "default" : "primary"}
              size="small"
              onClick={() => handleToggle(task)}
              style={{
                background: task.completed ? '#f5f5f5' : '#52c41a',
                borderColor: task.completed ? '#d9d9d9' : '#52c41a',
                color: task.completed ? '#999' : '#fff',
                borderRadius: '6px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {task.completed ? '↩️ Réouvrir' : '✓ Terminer'}
            </Button>
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px dashed #e8e8e8',
            animation: 'fadeIn 0.2s ease',
          }}>
            {task.description ? (
              <div style={{
                fontSize: '14px',
                color: task.completed ? '#bbb' : '#666',
                textDecoration: task.completed ? 'line-through' : 'none',
                marginBottom: '12px',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
              }}>
                {task.description}
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: '#bbb', fontStyle: 'italic', marginBottom: '12px' }}>
                Pas de description
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
              <Button size="small" icon={<EditOutlined />} onClick={(e) => handleEdit(e, task)}>
                Modifier
              </Button>
              <Popconfirm title="Supprimer cette tâche ?" onConfirm={(e) => handleDelete(e, task.id, task.type)} okText="Oui" cancelText="Non">
                <Button size="small" icon={<DeleteOutlined />} danger>
                  Supprimer
                </Button>
              </Popconfirm>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleClearCompleted = async (type) => {
    const tasksToDelete = type === 'today'
      ? todayTasks.filter(t => t.completed)
      : longTermTasks.filter(t => t.completed);

    if (tasksToDelete.length === 0) return;

    // Optimistic update
    if (type === 'today') {
      setTodayTasks(prev => prev.filter(t => !t.completed));
    } else {
      setLongTermTasks(prev => prev.filter(t => !t.completed));
    }

    // Delete from database
    const ids = tasksToDelete.map(t => t.id);
    await supabase.from('tasks').delete().in('id', ids);
    message.success(`${tasksToDelete.length} tâche(s) supprimée(s)`);
  };

  const Column = ({ title, tasks, type, icon, color }) => {
    const active = tasks.filter(t => !t.completed);
    const completed = tasks.filter(t => t.completed);

    return (
      <div style={{
        background: '#f5f5f5',
        borderRadius: '12px',
        padding: '16px',
        minHeight: '400px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '2px solid #e8e8e8',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>{icon}</span>
            <span style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>{title}</span>
            {active.length > 0 && (
              <span style={{
                background: color,
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '10px',
                minWidth: '24px',
                textAlign: 'center',
              }}>
                {active.length}
              </span>
            )}
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            onClick={() => handleAdd(type)}
            style={{ background: color, borderColor: color, borderRadius: '6px' }}
          >
            Ajouter
          </Button>
        </div>

        {tasks.length === 0 || (active.length === 0 && hideCompleted) ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📝</div>
            <div style={{ fontSize: '13px' }}>Aucune tâche</div>
          </div>
        ) : (
          <>
            {active.map(task => <TaskItem key={task.id} task={task} />)}

            {completed.length > 0 && (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '16px 0 8px',
                  paddingTop: '12px',
                  borderTop: '1px dashed #ddd',
                }}>
                  <Button
                    type="text"
                    size="small"
                    onClick={() => setHideCompleted(!hideCompleted)}
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '0 4px',
                    }}
                  >
                    {hideCompleted ? '👁️ Afficher' : '👁️ Masquer'} terminées ({completed.length})
                  </Button>

                  <Popconfirm
                    title={`Supprimer ${completed.length} tâche(s) terminée(s) ?`}
                    description="Cette action est irréversible"
                    onConfirm={() => handleClearCompleted(type)}
                    okText="Supprimer"
                    cancelText="Annuler"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      style={{ fontSize: '11px', padding: '0 4px' }}
                    >
                      🗑️ Vider
                    </Button>
                  </Popconfirm>
                </div>

                {!hideCompleted && completed.map(task => <TaskItem key={task.id} task={task} />)}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff', padding: '32px' }}>
      <audio ref={audioRef} src={NOTIFICATION_SOUND} preload="auto" />

      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #ff7a45, #fa541c)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '24px',
            }}>
              <FireOutlined />
            </div>
            <div>
              <Title level={3} style={{ margin: 0 }}>Sur le Feu</Title>
              <Text type="secondary">Tâches de l'équipe</Text>
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: '#fafafa',
            padding: '8px 16px',
            borderRadius: '8px',
          }}>
            <BellOutlined style={{ color: notificationsEnabled ? '#ff7a45' : '#999' }} />
            <span style={{ fontSize: '13px', color: '#666' }}>Notifications</span>
            <Switch
              checked={notificationsEnabled}
              onChange={toggleNotifications}
              size="small"
              style={{ background: notificationsEnabled ? '#ff7a45' : undefined }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <Column title="Aujourd'hui" tasks={todayTasks} type="today" icon="📅" color="#1890ff" />
          <Column title="Long Terme" tasks={longTermTasks} type="long_term" icon="🔥" color="#ff7a45" />
        </div>

        <Modal
          title={<span><FireOutlined style={{ color: '#ff7a45', marginRight: '8px' }} />{editingTask ? 'Modifier' : 'Nouvelle tâche'}</span>}
          open={modalVisible}
          onCancel={() => { setModalVisible(false); form.resetFields(); }}
          footer={[
            <Button key="cancel" onClick={() => { setModalVisible(false); form.resetFields(); }}>Annuler</Button>,
            <Button key="save" type="primary" onClick={handleSave} style={{ background: '#ff7a45', borderColor: '#ff7a45' }}>Enregistrer</Button>
          ]}
          width={500}
        >
          <Form form={form} layout="vertical" style={{ marginTop: '16px' }}>
            <Form.Item name="title" label="Titre" rules={[{ required: true, message: 'Requis' }]}>
              <Input placeholder="Titre de la tâche" size="large" />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <TextArea placeholder="Détails..." rows={2} />
            </Form.Item>

            <Form.Item name="created_by" label="Par" rules={[{ required: true, message: 'Requis' }]}>
              <Select placeholder="Qui ?" size="large" showSearch>
                {TEAM_MEMBERS.map(m => <Option key={m} value={m}>{m}</Option>)}
              </Select>
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item name="date" label="Date">
                <DatePicker format="DD/MM/YYYY" placeholder="Date" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="notification_time" label="Rappel">
                <TimePicker format="HH:mm" placeholder="Heure" style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default SurLeFeu;
